import { z } from 'zod'
import { JobPriority, JobStatus } from '../enums'
import { Id, Timestamp } from './common'

const text = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional()

export const JobPart = z.object({
  id: Id,
  spare_id: Id.nullable(),
  description: z.string().nullable(),
  qty: z.number().int(),
})
export type JobPart = z.infer<typeof JobPart>

export const Job = z.object({
  id: Id,
  bike_id: Id,
  bike_name: z.string(),
  component_id: Id.nullable(),
  title: z.string(),
  notes: z.string().nullable(),
  priority: JobPriority,
  status: JobStatus,
  started_at: Timestamp.nullable(),
  completed_at: Timestamp.nullable(),
  capture_ids: z.array(Id),
  parts: z.array(JobPart),
  steps: z.array(z.object({ text: z.string(), done: z.boolean() })),
  service_record_id: Id.nullable(),
  created_at: Timestamp,
  updated_at: Timestamp,
})
export type Job = z.infer<typeof Job>

export const JobList = z.object({ jobs: z.array(Job) })
export type JobList = z.infer<typeof JobList>

export const JobPartInput = z
  .object({
    spare_id: Id.nullable().optional(),
    description: text(200),
    qty: z.number().int().min(1).max(999).default(1),
  })
  .refine((p) => p.spare_id || p.description, 'Name the part or pick a spare.')
export type JobPartInput = z.input<typeof JobPartInput>

export const CreateJobBody = z.object({
  bike_id: Id,
  component_id: Id.nullable().optional(),
  title: z.string().trim().min(1, 'Give the job a title.').max(200),
  notes: text(4000),
  priority: JobPriority.default('normal'),
  /** Captures this job was made from; they move out of the inbox. */
  capture_ids: z.array(Id).max(50).default([]),
  parts: z.array(JobPartInput).max(50).default([]),
})
export type CreateJobBody = z.input<typeof CreateJobBody>

/** Status changes go through start / complete / cancel / reopen, not PATCH. */
export const UpdateJobBody = z
  .object({
    title: z.string().trim().min(1).max(200),
    notes: text(4000),
    priority: JobPriority,
    component_id: Id.nullable(),
  })
  .partial()
  .refine((b) => Object.keys(b).length > 0, 'Nothing to update.')
export type UpdateJobBody = z.input<typeof UpdateJobBody>

export const JobListQuery = z.object({
  bike_id: Id.optional(),
  status: z.enum(['open', 'in_progress', 'done', 'cancelled', 'active']).optional(),
})

export const MergeCapturesBody = z.object({ capture_ids: z.array(Id).min(1).max(50) })

/**
 * Completing a job always writes the service log (CLAUDE.md rule 5). These
 * fields shape that entry; all are optional with sensible defaults.
 */
export const CompleteJobBody = z.object({
  performed_at: Timestamp.optional(),
  description: text(4000),
  cost_minor: z.number().int().min(0).max(100_000_000).nullable().optional(),
  currency: z
    .string()
    .regex(/^[A-Z]{3}$/)
    .default('GBP'),
  /** Parts worked on. Defaults to the job's part, if any. */
  component_ids: z.array(Id).max(50).optional(),
  /** Service intervals this work resets, at `performed_at`. */
  reset_interval_ids: z.array(Id).max(50).default([]),
})
export type CompleteJobBody = z.input<typeof CompleteJobBody>

export const JobPartParams = z.object({ id: Id, partId: Id })
